import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';
import { tr } from '../i18n';
import Logo from '../components/Logo';
import ExtrasBox from '../components/ExtrasBox';
import { getAllergens, getExtras } from '../utils/menuExtras';
import { ArrowLeft, Printer } from 'lucide-react';

const PrintPage = () => {
  const navigate = useNavigate();
  const { menu, lang, t } = useApp();

  useEffect(() => {
    document.title = `Il Ponticello · ${lang === 'it' ? 'Menù da stampare' : 'Printable Menu'}`;
    return () => { document.title = 'Il Ponticello · Menù'; };
  }, [lang]);

  const doPrint = () => window.print();
  const extras = getExtras(menu);

  // Flatten sections in the order defined by mainCategories + subCategories
  const sections = [];
  for (const cat of menu.mainCategories) {
    for (const sub of (menu.subCategories[cat.slug] || [])) {
      const raw = menu.items && menu.items[sub.slug];
      const items = ((raw && raw.items) || []).filter((it) => it.visible !== false);
      if (items.length) {
        sections.push({ cat, sub, data: { ...raw, items } });
      }
    }
  }

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-black/15">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 px-4 py-3">
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 rounded-full border-[1.5px] border-black flex items-center justify-center hover:bg-black hover:text-white transition"
            aria-label={t('back')}
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex items-center gap-2">
            <Logo height={32} />
            <span className="font-display text-xl hidden sm:inline">{lang === 'it' ? 'Menù da stampare' : 'Printable menu'}</span>
          </div>
          <button onClick={doPrint} className="pill-btn primary">
            <Printer size={16} /> {lang === 'it' ? 'Stampa / Salva PDF' : 'Print / Save PDF'}
          </button>
        </div>
        <p className="max-w-5xl mx-auto px-4 pb-3 text-xs text-black/60">
          {lang === 'it'
            ? 'Dalla finestra di stampa seleziona “Salva come PDF” per generare un PDF. Impostazioni consigliate: A4, margini stretti, scala 100%.'
            : 'In the print dialog pick “Save as PDF” to export. Suggested settings: A4, narrow margins, 100% scale.'}
        </p>
      </div>

      {/* Printable sheet */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 print:px-10 print:py-6">
        {/* Header */}
        <header className="text-center mb-10 print:mb-6">
          <div className="flex items-center justify-center mb-4">
            <Logo height={72} />
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-black/60 mb-2">
            {lang === 'it' ? 'Pinseria & Birre artigianali' : 'Pinseria & Craft beers'}
          </p>
          <h1 className="font-display text-5xl md:text-6xl print:text-5xl">
            {lang === 'it' ? (<>Il <span className="font-serif-alt">nostro</span> Menù</>) : (<>Our <span className="font-serif-alt">Menu</span></>)}
          </h1>
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="h-px w-10 bg-black" />
            <span className="text-xs font-bold uppercase tracking-widest">{menu.restaurantInfo?.address}</span>
            <span className="h-px w-10 bg-black" />
          </div>
        </header>

        {/* Sections */}
        <div className="space-y-10 print:space-y-6">
          {sections.map(({ cat, sub, data }, idx) => (
            <section key={sub.slug} className={`${idx > 0 && (sub.slug === 'pinse-tradizionali' || sub.slug === 'birre-spina' || sub.slug === 'caffe') ? 'print:break-before-page' : ''}`}>
              <div className="mb-3 flex items-baseline justify-between gap-4 border-b border-black pb-2">
                <h2 className="font-display text-3xl md:text-4xl print:text-3xl" translate="no">
                  {tr(data.title, lang)}
                </h2>
                <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-black/50">{tr(cat.name, lang)}</span>
              </div>
              {data.note && (
                <p className="text-xs italic text-black/70 mb-3">{tr(data.note, lang)}</p>
              )}
              <ul className="divide-y divide-black/15">
                {data.items.map((it, i) => (
                  <li key={it._id || i} className="py-2.5 print:py-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 translate="no" className="notranslate font-display text-lg leading-tight">
                        {tr(it.name, lang)}
                      </h3>
                      <div className="flex-1 mx-2 border-b border-dotted border-black/30 translate-y-[-4px]" />
                      <span className="font-display text-base whitespace-nowrap">€ {it.price}</span>
                    </div>
                    {tr(it.desc, lang) && (
                      <p translate="no" className="notranslate text-[12px] text-black/75 leading-snug mt-0.5 pr-4">
                        {tr(it.desc, lang)}
                      </p>
                    )}
                    {it.allergens && (
                      <p className="text-[10px] text-black/50 font-bold uppercase tracking-wider mt-0.5">
                        {t('allergens')}: {it.allergens}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
              {extras.sections.includes(sub.slug) && <ExtrasBox items={extras.items} lang={lang} compact />}
            </section>
          ))}
        </div>

        {/* Allergens legend */}
        <section className="mt-10 border-t border-black pt-4">
          <h3 className="text-xs font-bold uppercase tracking-[0.28em] text-black/70 mb-3">
            {t('allergensLegendTitle')}
          </h3>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-1">
            {getAllergens(menu).map((a) => (
              <div key={a.n} className="flex items-baseline gap-2 text-xs">
                <span className="font-display text-sm w-5 text-right">{a.n}</span>
                <span>{tr(a.name, lang)}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-10 text-center text-[11px] text-black/60">
          Il Ponticello — {menu.restaurantInfo?.address} • {menu.restaurantInfo?.phone} • {tr(menu.restaurantInfo?.hours, lang)}
        </footer>
      </div>

      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .print\\:break-before-page { break-before: page; page-break-before: always; }
        }
      `}</style>
    </div>
  );
};

export default PrintPage;
